import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { videosAPI, cursosAPI, modulosAPI } from '../../services/api';
import { 
  Video, 
  Edit, 
  Trash2, 
  Plus, 
  Search, 
  Clock, 
  Hash, 
  PlayCircle,
  BookOpen,
  Layers,
  AlertTriangle,
  Save,
  X
} from 'lucide-react';

function GerenciarVideos() {
  const [videos, setVideos] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [cursoFilter, setCursoFilter] = useState('');
  const [editandoVideo, setEditandoVideo] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    ordem: 1,
    duracao: '',
    curso_id: '',
    modulo_id: ''
  });
  const [videoParaDeletar, setVideoParaDeletar] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (cursoFilter) {
      carregarModulos(cursoFilter);
    } else {
      setModulos([]);
    }
  }, [cursoFilter]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [cursosResponse] = await Promise.all([
        cursosAPI.listar()
      ]);
      
      setCursos(cursosResponse.data || []);
      
      // Carregar vídeos de todos os cursos
      const todosVideos = [];
      if (cursosResponse.data) {
        for (const curso of cursosResponse.data) {
          try {
            const videosResponse = await videosAPI.listarPorCurso(curso.id);
            const videosComCurso = videosResponse.data.map(video => ({
              ...video,
              curso_titulo: curso.titulo
            }));
            todosVideos.push(...videosComCurso);
          } catch (err) {
            console.error(`Erro ao carregar vídeos do curso ${curso.id}:`, err);
          }
        }
      }
      
      setVideos(todosVideos);
    } catch (err) {
      setError('Erro ao carregar dados');
      console.error('Erro:', err);
    } finally {
      setLoading(false);
    }
  };

  const carregarModulos = async (cursoId) => {
    try {
      const response = await modulosAPI.listarPorCurso(cursoId);
      setModulos(response.data.modulos || []);
    } catch (err) {
      console.error('Erro ao carregar módulos:', err);
      setModulos([]);
    }
  };

  const iniciarEdicao = (video) => {
    setEditandoVideo(video.id);
    setFormData({
      titulo: video.titulo || '',
      descricao: video.descricao || '',
      ordem: video.ordem || 1,
      duracao: video.duracao || '',
      curso_id: video.curso_id || '',
      modulo_id: video.modulo_id || ''
    });
    
    if (video.curso_id) {
      carregarModulos(video.curso_id);
    }
  };

  const cancelarEdicao = () => {
    setEditandoVideo(null);
    setFormData({
      titulo: '',
      descricao: '',
      ordem: 1,
      duracao: '',
      curso_id: '',
      modulo_id: ''
    });
  };

  const salvarEdicao = async () => {
    try {
      const dadosParaEnviar = {
        ...formData,
        curso_id: parseInt(formData.curso_id),
        modulo_id: formData.modulo_id ? parseInt(formData.modulo_id) : null,
        ordem: parseInt(formData.ordem) || 1,
        duracao: formData.duracao ? parseInt(formData.duracao) : null
      };

      await videosAPI.atualizar(editandoVideo, dadosParaEnviar);
      await carregarDados();
      cancelarEdicao();
    } catch (err) {
      setError('Erro ao atualizar vídeo');
      console.error('Erro:', err);
    }
  };

  const confirmarDelecao = (video) => {
    setVideoParaDeletar(video);
  };

  const deletarVideo = async () => {
    if (!videoParaDeletar) return;

    try {
      await videosAPI.deletar(videoParaDeletar.id);
      await carregarDados();
      setVideoParaDeletar(null);
    } catch (err) {
      setError('Erro ao deletar vídeo');
      console.error('Erro:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Se mudou o curso, limpar módulo e carregar novos módulos
    if (name === 'curso_id') {
      setFormData(prev => ({ ...prev, modulo_id: '' }));
      if (value) {
        carregarModulos(value);
      } else {
        setModulos([]);
      }
    }
  };

  const formatarDuracao = (segundos) => {
    if (!segundos) return '-';
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos}:${segs.toString().padStart(2, '0')}`;
  };

  const filteredVideos = videos.filter(video => {
    const matchSearch = video.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       video.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCurso = !cursoFilter || video.curso_id?.toString() === cursoFilter;
    return matchSearch && matchCurso;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-4">
            <div className="w-12 h-12 border-4 border-slate-700 border-t-red-600 rounded-full animate-spin mx-auto"></div>
            <p className="text-slate-300">Carregando vídeos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <div className="w-12 h-12 bg-red-600/20 rounded-xl flex items-center justify-center">
                  <Video className="w-6 h-6 text-red-400" />
                </div>
                Gerenciar Vídeos
              </h1>
              <p className="text-slate-400 mt-2">
                Gerencie todos os vídeos dos seus cursos
              </p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-800/50 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Buscar vídeos
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite o título ou descrição..."
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600/50"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                <BookOpen className="w-4 h-4 inline mr-1" />
                Filtrar por curso
              </label>
              <select
                value={cursoFilter}
                onChange={(e) => setCursoFilter(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-600/50"
              >
                <option value="">Todos os cursos</option>
                {cursos.map(curso => (
                  <option key={curso.id} value={curso.id}>
                    {curso.titulo}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{filteredVideos.length}</div>
                <div className="text-sm text-slate-400">vídeos encontrados</div>
              </div>
            </div>
          </div>
        </div>

        {/* Lista de Vídeos */}
        {error && (
          <div className="bg-red-600/10 border border-red-600/20 rounded-lg p-4 text-red-400">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {filteredVideos.length === 0 ? (
            <Card className="p-8 text-center bg-slate-900/50 border-slate-800/50">
              <Video className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Nenhum vídeo encontrado</h3>
              <p className="text-slate-400">
                {searchTerm || cursoFilter ? 'Tente ajustar os filtros de busca.' : 'Ainda não há vídeos cadastrados.'}
              </p>
            </Card>
          ) : (
            filteredVideos.map((video) => (
              <Card key={video.id} className="p-6 bg-slate-900/50 border-slate-800/50 backdrop-blur-xl">
                {editandoVideo === video.id ? (
                  // Modo de edição
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Edit className="w-5 h-5 text-blue-400" />
                        Editando Vídeo
                      </h3>
                      <div className="flex gap-2">
                        <Button
                          onClick={salvarEdicao}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Save className="w-4 h-4 mr-1" />
                          Salvar
                        </Button>
                        <Button
                          onClick={cancelarEdicao}
                          size="sm"
                          className="bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Título *</label>
                        <input
                          type="text"
                          name="titulo"
                          value={formData.titulo}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Curso *</label>
                        <select
                          name="curso_id"
                          value={formData.curso_id}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-600/50"
                        >
                          <option value="">Selecione um curso</option>
                          {cursos.map(curso => (
                            <option key={curso.id} value={curso.id}>
                              {curso.titulo}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Módulo</label>
                        <select
                          name="modulo_id"
                          value={formData.modulo_id}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-600/50"
                        >
                          <option value="">Sem módulo</option>
                          {modulos.map(modulo => (
                            <option key={modulo.id} value={modulo.id}>
                              {modulo.titulo}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Ordem</label>
                        <input
                          type="number"
                          name="ordem"
                          value={formData.ordem}
                          onChange={handleInputChange}
                          min="1"
                          className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/50"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-300 mb-1">Descrição</label>
                        <textarea
                          name="descricao"
                          value={formData.descricao}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/50 resize-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Duração (segundos)</label>
                        <input
                          type="number"
                          name="duracao"
                          value={formData.duracao}
                          onChange={handleInputChange}
                          min="0"
                          className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/50"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  // Modo de visualização
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center">
                          <PlayCircle className="w-6 h-6 text-red-400" />
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">
                            {video.titulo}
                          </h3>
                          
                          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-2">
                            <div className="flex items-center gap-1">
                              <BookOpen className="w-3 h-3" />
                              {video.curso_titulo}
                            </div>
                            <div className="flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              Ordem: {video.ordem}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatarDuracao(video.duracao)}
                            </div>
                            {video.modulo_id && (
                              <Badge className="text-xs bg-blue-600/20 text-blue-400 border border-blue-600/50">
                                <Layers className="w-3 h-3 mr-1" />
                                Com Módulo
                              </Badge>
                            )}
                          </div>
                          
                          {video.descricao && (
                            <p className="text-slate-300 text-sm line-clamp-2">
                              {video.descricao}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => iniciarEdicao(video)}
                        size="sm"
                        className="bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-600/50"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => confirmarDelecao(video)}
                        size="sm"
                        className="bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Modal de confirmação de exclusão */}
      {videoParaDeletar && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4 p-6 bg-slate-900 border-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Confirmar Exclusão</h3>
                <p className="text-slate-400 text-sm">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            
            <p className="text-slate-300 mb-6">
              Tem certeza que deseja excluir o vídeo "{videoParaDeletar.titulo}"?
            </p>
            
            <div className="flex gap-3">
              <Button
                onClick={deletarVideo}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir
              </Button>
              <Button
                onClick={() => setVideoParaDeletar(null)}
                className="flex-1 bg-slate-700/50 hover:bg-slate-600 text-slate-200 border border-slate-600"
              >
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default GerenciarVideos; 